import { Link } from 'react-router-dom';
import useDeckNavigation from '../hooks/useDeckNavigation';

const SLIDES = [
  {
    id: 'cover',
    title: 'Cover',
    render: () => (
      <div className="pitch-slide pitch-cover">
        <div className="pitch-cover-chip">Investor Overview</div>
        <img src="/logo512.png" alt="10 Thirty" className="pitch-cover-logo" />
        <h1 className="pitch-cover-title">10 Thirty</h1>
        <p className="pitch-cover-tagline">Private communication without the archive.</p>
        <p className="pitch-cover-sub">Created in South Africa. Built for a global market.</p>
      </div>
    ),
  },
  {
    id: 'problem',
    title: 'Problem',
    render: () => (
      <div className="pitch-slide pitch-content">
        <p className="pitch-label">The Problem</p>
        <h2 className="pitch-heading">Sensitive conversations still happen on systems designed to keep everything.</h2>
        <div className="pitch-body">
          <p>
            Teams discuss strategy, legal matters, negotiations, finance, hiring, and personal
            issues on platforms built for retention, not controlled communication.
          </p>
          <p>
            Permanent threads create legal exposure, breach exposure, internal risk, and the wrong
            communication behaviour. People speak differently when they know the archive is the real audience.
          </p>
        </div>
        <div className="pitch-stat-row">
          <div className="pitch-stat">
            <span className="pitch-stat-num">Always On</span>
            <span className="pitch-stat-label">Most messaging products default to permanent access and persistent history</span>
          </div>
          <div className="pitch-stat">
            <span className="pitch-stat-num">High Risk</span>
            <span className="pitch-stat-label">Sensitive discussions become discoverable, leakable, and screenshotable records</span>
          </div>
          <div className="pitch-stat">
            <span className="pitch-stat-num">Wrong Medium</span>
            <span className="pitch-stat-label">Professional trust conversations are being forced into generic chat infrastructure</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'why-now',
    title: 'Why Now',
    render: () => (
      <div className="pitch-slide pitch-content">
        <p className="pitch-label">Why Now</p>
        <h2 className="pitch-heading">The world just made our argument for us.</h2>
        <div className="pitch-body">
          <p>
            After the Salt Typhoon telecom breach, the FBI and CISA publicly urged people onto
            end-to-end encrypted apps — CISA's official guidance explicitly endorses disappearing
            messages. Ephemeral encrypted messaging went from suspicious to state-recommended in
            under a year.
          </p>
          <p>
            AI made permanence a liability: a US federal court ordered OpenAI to preserve users&rsquo;
            deleted chats, and Meta now feeds AI conversations into ad targeting. Stored
            conversations are no longer just a breach risk — they are evidence and training data.
          </p>
        </div>
        <div className="pitch-stat-row">
          <div className="pitch-stat">
            <span className="pitch-stat-num">R44m</span>
            <span className="pitch-stat-label">Average cost of a data breach in South Africa (IBM, 2025)</span>
          </div>
          <div className="pitch-stat">
            <span className="pitch-stat-num">2,374</span>
            <span className="pitch-stat-label">Breaches reported to SA's Information Regulator in FY2024/25 — this year is tracking 40% higher</span>
          </div>
          <div className="pitch-stat">
            <span className="pitch-stat-num">#1</span>
            <span className="pitch-stat-label">South Africa is Africa's most-targeted country for cyberattacks (INTERPOL, 2025)</span>
          </div>
        </div>
        <div className="pitch-note">What isn't stored can't be breached, subpoenaed, or trained on.</div>
      </div>
    ),
  },
  {
    id: 'solution',
    title: 'Solution',
    render: () => (
      <div className="pitch-slide pitch-content">
        <p className="pitch-label">The Solution</p>
        <h2 className="pitch-heading">10 Thirty creates controlled, encrypted, time-bound conversations.</h2>
        <div className="pitch-how-it-works">
          <div className="pitch-step">
            <span className="pitch-step-num">01</span>
            <span className="pitch-step-text">Open a tunnel for chat, voice, or video</span>
          </div>
          <div className="pitch-step-arrow">&rarr;</div>
          <div className="pitch-step">
            <span className="pitch-step-num">02</span>
            <span className="pitch-step-text">Both parties consent. Messages and media are end-to-end encrypted; calls run on encrypted infrastructure</span>
          </div>
          <div className="pitch-step-arrow">&rarr;</div>
          <div className="pitch-step">
            <span className="pitch-step-num">03</span>
            <span className="pitch-step-text">The session ends. The tunnel closes. The archive does not remain behind</span>
          </div>
        </div>
        <div className="pitch-body pitch-body-small">
          <p>
            10 Thirty is not generic chat. It is communication infrastructure for moments that need
            privacy, consent, and closure.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'product',
    title: 'Product',
    render: () => (
      <div className="pitch-slide pitch-content">
        <p className="pitch-label">Product</p>
        <h2 className="pitch-heading">Live product. Real security architecture. Clear category difference.</h2>
        <div className="pitch-features">
          <div className="pitch-feature">
            <span className="pitch-feature-icon">🔐</span>
            <span className="pitch-feature-title">Signal Protocol</span>
            <span className="pitch-feature-desc">X3DH plus Double Ratchet for forward-secret messaging.</span>
          </div>
          <div className="pitch-feature">
            <span className="pitch-feature-icon">📞</span>
            <span className="pitch-feature-title">Voice and Video</span>
            <span className="pitch-feature-desc">Encrypted call flows inside the same tunnel model, not a separate communication stack.</span>
          </div>
          <div className="pitch-feature">
            <span className="pitch-feature-icon">🛡️</span>
            <span className="pitch-feature-title">Consent-Based Contact</span>
            <span className="pitch-feature-desc">No unsolicited messaging. Every conversation starts with mutual acceptance.</span>
          </div>
          <div className="pitch-feature">
            <span className="pitch-feature-icon">⌛</span>
            <span className="pitch-feature-title">Time-Bound by Design</span>
            <span className="pitch-feature-desc">Tunnels and message TTLs create expiry and closure at the product level.</span>
          </div>
          <div className="pitch-feature">
            <span className="pitch-feature-icon">👥</span>
            <span className="pitch-feature-title">Group Tunnels and Seats</span>
            <span className="pitch-feature-desc">Business subscribers run board discussions with up to 10 participants, scheduled with accept/reject. Seat management centralises team billing.</span>
          </div>
          <div className="pitch-feature">
            <span className="pitch-feature-icon">🆘</span>
            <span className="pitch-feature-title">SOS</span>
            <span className="pitch-feature-desc">Consent-paired emergency alert to up to three trusted contacts, with an optional location share and a one-tap "I'm fine" stand-down. Available on every tier.</span>
          </div>
          <div className="pitch-feature">
            <span className="pitch-feature-icon">🕊️</span>
            <span className="pitch-feature-title">Pigeons and Nests</span>
            <span className="pitch-feature-desc">Reusable private audiences for one-shot broadcast messages. Each recipient gets their own ephemeral copy — no group chat archive, no shared thread. Tier-scaled from 2 Nests on Starter to unlimited on Pro and Business.</span>
          </div>
          <div className="pitch-feature">
            <span className="pitch-feature-icon">🔗</span>
            <span className="pitch-feature-title">Ubuntu Program</span>
            <span className="pitch-feature-desc">Community growth engine: referral commissions, free tunnel rewards, and a bug bounty that pays users to improve the product.</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'market-position',
    title: 'Positioning',
    render: () => (
      <div className="pitch-slide pitch-content">
        <p className="pitch-label">Positioning</p>
        <h2 className="pitch-heading">Created in South Africa. Positioned for international demand.</h2>
        <div className="pitch-body">
          <p>
            The origin story matters because it is credible. The market story matters because it is global.
            Privacy-sensitive communication is not a regional problem. Legal, finance, advisory, executive,
            and privacy-conscious users exist in every major market.
          </p>
          <p>
            South Africa is the origin and early credibility base. The product and commercial framing target
            a broader international market where controlled communication has clear value.
          </p>
        </div>
        <div className="pitch-stat-row">
          <div className="pitch-stat">
            <span className="pitch-stat-num">Global</span>
            <span className="pitch-stat-label">The need is category-wide, not country-specific</span>
          </div>
          <div className="pitch-stat">
            <span className="pitch-stat-num">Focused</span>
            <span className="pitch-stat-label">Initial segments are high-trust professional and privacy-sensitive users</span>
          </div>
          <div className="pitch-stat">
            <span className="pitch-stat-num">Credible</span>
            <span className="pitch-stat-label">Built from a market that already understands privacy, regulation, and infrastructure constraints</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'business-model',
    title: 'Business Model',
    render: () => (
      <div className="pitch-slide pitch-content">
        <p className="pitch-label">Business Model</p>
        <h2 className="pitch-heading">Freemium SaaS.</h2>
        <div className="pitch-topline">
          Free gets users in. Subscriptions create recurring revenue.
        </div>
        <div className="pitch-tiers">
          <div className="pitch-tier">
            <p className="pitch-tier-name">Starter</p>
            <p className="pitch-tier-desc">Free tier for acquisition. Limited daily tunnels and contacts. Entry point into the product.</p>
          </div>
          <div className="pitch-tier pitch-tier-highlight">
            <p className="pitch-tier-name">Pro</p>
            <p className="pitch-tier-desc">Subscription for individual professionals who need recurring access and stronger control.</p>
          </div>
          <div className="pitch-tier">
            <p className="pitch-tier-name">Business</p>
            <p className="pitch-tier-desc">Higher-value subscription with group tunnels for up to 10 participants, team seat management, and configurable durations up to 20 minutes.</p>
          </div>
        </div>
        <div className="pitch-body pitch-body-small">
          <p>
            Business subscribers can purchase seats for their team, assigning Pro or Business access per member
            and centralising billing on a single account.
          </p>
          <p>
            The Ubuntu Program drives community-led acquisition: referral commissions, free tunnel rewards
            for both parties, and a bug bounty that pays users to improve the product. Growth and
            quality improvement from the same community loop.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'pricing',
    title: 'Pricing',
    render: () => (
      <div className="pitch-slide pitch-content">
        <p className="pitch-label">Pricing Strategy</p>
        <h2 className="pitch-heading">Launch urgency now. Stronger list pricing later.</h2>
        <div className="pitch-kpi-grid">
          <div className="pitch-kpi pitch-kpi-highlight">
            <span className="pitch-kpi-label">Launch Special</span>
            <strong className="pitch-kpi-value">50% Off</strong>
            <span className="pitch-kpi-copy">Valid until September 30. Early subscribers keep the launch price.</span>
          </div>
          <div className="pitch-kpi">
            <span className="pitch-kpi-label">Pro</span>
            <strong className="pitch-kpi-value">$15 <span className="pitch-kpi-muted">→ $30</span></strong>
            <span className="pitch-kpi-copy">Launch price now, full list price after September 30.</span>
          </div>
          <div className="pitch-kpi">
            <span className="pitch-kpi-label">Business</span>
            <strong className="pitch-kpi-value">$29 <span className="pitch-kpi-muted">→ $58</span></strong>
            <span className="pitch-kpi-copy">Higher-value tier with the same grandfathering logic.</span>
          </div>
        </div>
        <div className="pitch-tiers">
          <div className="pitch-tier pitch-tier-highlight">
            <p className="pitch-tier-name">Grandfathering</p>
            <p className="pitch-tier-desc">Subscribers who join during launch keep their $15 or $29 renewal price.</p>
          </div>
          <div className="pitch-tier">
            <p className="pitch-tier-name">Post-Launch</p>
            <p className="pitch-tier-desc">New customers after September 30 pay the full list price of $30 or $58.</p>
          </div>
        </div>
        <div className="pitch-note">Urgency drives conversion. Grandfathering preserves trust.</div>
      </div>
    ),
  },
  {
    id: 'onboarding-mix',
    title: 'Onboarding',
    render: () => (
      <div className="pitch-slide pitch-content">
        <p className="pitch-label">Onboarding Model</p>
        <h2 className="pitch-heading">We expect an 85 / 12 / 3 onboarding mix.</h2>
        <div className="pitch-kpi-grid">
          <div className="pitch-kpi pitch-kpi-highlight">
            <span className="pitch-stat-num">85%</span>
            <span className="pitch-stat-label">Starter users entering through the free tier</span>
          </div>
          <div className="pitch-kpi">
            <span className="pitch-stat-num">12%</span>
            <span className="pitch-stat-label">Pro subscribers converting into recurring revenue</span>
          </div>
          <div className="pitch-kpi">
            <span className="pitch-stat-num">3%</span>
            <span className="pitch-stat-label">Business subscribers at higher-value contract economics</span>
          </div>
        </div>
        <div className="pitch-panel">
          <p className="pitch-panel-title">Expected Range</p>
          <p className="pitch-panel-copy">Starter 80 to 90 percent. Pro 8 to 15 percent. Business 1 to 5 percent.</p>
          <p className="pitch-panel-copy">The commercial model assumes most users enter free while a smaller paid cohort drives MRR.</p>
        </div>
      </div>
    ),
  },
  {
    id: 'revenue',
    title: 'Revenue',
    render: () => (
      <div className="pitch-slide pitch-content">
        <p className="pitch-label">Revenue Example</p>
        <h2 className="pitch-heading">At 1,000 onboarded users, the model starts to show its shape.</h2>
        <div className="pitch-kpi-grid">
          <div className="pitch-kpi">
            <span className="pitch-stat-num">850</span>
            <span className="pitch-stat-label">Starter users</span>
          </div>
          <div className="pitch-kpi">
            <span className="pitch-stat-num">120</span>
            <span className="pitch-stat-label">Pro subscribers</span>
          </div>
          <div className="pitch-kpi">
            <span className="pitch-stat-num">30</span>
            <span className="pitch-stat-label">Business subscribers</span>
          </div>
        </div>
        <div className="pitch-revenue-band">
          <div className="pitch-revenue-card pitch-revenue-card-highlight">
            <span className="pitch-revenue-label">Launch MRR</span>
            <strong className="pitch-revenue-value">$2,670</strong>
            <span className="pitch-revenue-copy">$1,800 Pro + $870 Business</span>
          </div>
          <div className="pitch-revenue-card">
            <span className="pitch-revenue-label">Post-Launch New-Customer MRR</span>
            <strong className="pitch-revenue-value">$5,340</strong>
            <span className="pitch-revenue-copy">Same mix at $30 Pro and $58 Business</span>
          </div>
        </div>
        <div className="pitch-note">Subscription MRR scales predictably with acquisition.</div>
      </div>
    ),
  },
  {
    id: 'segments',
    title: 'Segments',
    render: () => (
      <div className="pitch-slide pitch-content">
        <p className="pitch-label">Customer Segments</p>
        <h2 className="pitch-heading">Best fit is where discretion, trust, and boundaries matter.</h2>
        <div className="pitch-features">
          <div className="pitch-feature">
            <span className="pitch-feature-icon">⚖️</span>
            <span className="pitch-feature-title">Legal and Advisory</span>
            <span className="pitch-feature-desc">Privileged discussions and sensitive coordination that should not become archives.</span>
          </div>
          <div className="pitch-feature">
            <span className="pitch-feature-icon">💼</span>
            <span className="pitch-feature-title">Executives and Boards</span>
            <span className="pitch-feature-desc">Strategy, leadership, and decision-making conversations that need control and closure.</span>
          </div>
          <div className="pitch-feature">
            <span className="pitch-feature-icon">💰</span>
            <span className="pitch-feature-title">Finance and Deals</span>
            <span className="pitch-feature-desc">Market-sensitive and relationship-sensitive communication that should stay between the parties.</span>
          </div>
          <div className="pitch-feature">
            <span className="pitch-feature-icon">🧠</span>
            <span className="pitch-feature-title">Privacy-Conscious Individuals</span>
            <span className="pitch-feature-desc">Users who want a better social contract for communication, not just another chat app.</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'readiness',
    title: 'Readiness',
    render: () => (
      <div className="pitch-slide pitch-content">
        <p className="pitch-label">Product Readiness</p>
        <h2 className="pitch-heading">The product is live. The next step is distribution.</h2>
        <div className="pitch-traction-list">
          <div className="pitch-traction-item">
            <span className="pitch-traction-dot" />
            <span>Live on iOS and Android</span>
          </div>
          <div className="pitch-traction-item">
            <span className="pitch-traction-dot" />
            <span>End-to-end encrypted chat and media; encrypted voice and video calls</span>
          </div>
          <div className="pitch-traction-item">
            <span className="pitch-traction-dot" />
            <span>Subscription billing and recurring renewals live</span>
          </div>
          <div className="pitch-traction-item">
            <span className="pitch-traction-dot" />
            <span>Business seat management: owners assign Pro or Business per team member</span>
          </div>
          <div className="pitch-traction-item">
            <span className="pitch-traction-dot" />
            <span>Ubuntu Program: referral commissions, bug bounty rewards, and community-driven growth</span>
          </div>
          <div className="pitch-traction-item">
            <span className="pitch-traction-dot" />
            <span>Admin dashboard with real-time analytics, user management, and payment oversight</span>
          </div>
          <div className="pitch-traction-item">
            <span className="pitch-traction-dot" />
            <span>Production validation across backend, web, and mobile test suites</span>
          </div>
          <div className="pitch-traction-item">
            <span className="pitch-traction-dot" />
            <span>Category message is defined; the remaining work is adoption and market capture</span>
          </div>
        </div>
      </div>
    ),
  },
  {
    id: 'ask',
    title: 'Ask',
    render: () => (
      <div className="pitch-slide pitch-content">
        <p className="pitch-label">The Ask</p>
        <h2 className="pitch-heading">Raise capital to accelerate category ownership.</h2>
        <div className="pitch-topline">
          The product exists. The capital need is distribution, enterprise expansion, and infrastructure scale.
        </div>
        <div className="pitch-ask-grid">
          <div className="pitch-ask-item">
            <p className="pitch-ask-heading">Growth</p>
            <p className="pitch-ask-desc">Acquire the first high-intent users in legal, finance, executive, and privacy-conscious segments.</p>
          </div>
          <div className="pitch-ask-item">
            <p className="pitch-ask-heading">Distribution</p>
            <p className="pitch-ask-desc">Build a repeatable motion across product-led onboarding, direct outreach, partnerships, and trust content.</p>
          </div>
          <div className="pitch-ask-item">
            <p className="pitch-ask-heading">Enterprise Expansion</p>
            <p className="pitch-ask-desc">Seat management and admin dashboard are live. Next: compliance reporting, SSO, and deeper org controls for regulated industries.</p>
          </div>
          <div className="pitch-ask-item">
            <p className="pitch-ask-heading">Infrastructure</p>
            <p className="pitch-ask-desc">Scale reliability, call infrastructure, monitoring, and security posture as usage grows internationally.</p>
          </div>
        </div>
        <div className="pitch-contact">
          <p>10thirty.co.za</p>
        </div>
      </div>
    ),
  },
];

export default function PitchDeck() {
  const total = SLIDES.length;
  const { current, prev, next, stageProps } = useDeckNavigation(total);

  const slide = SLIDES[current];

  return (
    <div className="pitch-deck">
      <div className="pitch-stage" {...stageProps}>
        {slide.render()}
      </div>

      <div className="pitch-controls">
        <Link to="/" className="pitch-home-link" aria-label="Back to website">
          <img src="/logo192.png" alt="10 Thirty" className="pitch-home-logo" />
        </Link>
        <button
          className="pitch-nav-btn"
          onClick={prev}
          disabled={current === 0}
          aria-label="Previous slide"
        >
          &larr;
        </button>
        <div className="pitch-counter-wrap">
          <span className="pitch-counter-label">{slide.title}</span>
          <span className="pitch-counter">{current + 1} / {total}</span>
        </div>
        <button
          className="pitch-nav-btn"
          onClick={next}
          disabled={current === total - 1}
          aria-label="Next slide"
        >
          &rarr;
        </button>
      </div>

      <div className="pitch-progress-track">
        <div
          className="pitch-progress-fill"
          style={{ width: `${((current + 1) / total) * 100}%` }}
        />
      </div>
    </div>
  );
}
